import { CampaignAdsGenerateSpinner } from '@/components/campaign/overview/ads-generate-spinner';
import { useCampaignsTaskTracking } from '@/hooks/useAreAllTasksCompleted';
import { useRefetchCampaigns } from '@/hooks/useRefetchCampaign';
import { useInvalidateQuery } from '@/hooks/useInvalidateQuery';
import { campaignsDataAtom } from '@/store';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';

// Brief minimum display so a fast generation doesn't flash the spinner (#5093 —
// previously a flat 60s gate that blocked navigation even when tasks finished early)
const MIN_DISPLAY_MS = 5000;

export const Route = createFileRoute('/_authenticated/new/_layout/onboarding/generating')({
  component: OnboardingCampaignGeneratingComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    // Stripe success redirect appends ?session_id=... (#5093)
    session_id: (search.session_id as string) || undefined,
  }),
});

function OnboardingCampaignGeneratingComponent() {
  const navigate = useNavigate();
  const invalidateQuery = useInvalidateQuery();
  const [minTimeReached, setMinTimeReached] = useState(false);
  const navigatedRef = useRef(false);
  const { session_id: sessionId } = Route.useSearch();

  const campaigns = useAtomValue(campaignsDataAtom);
  const { sumTotal, taskProcessing } = useCampaignsTaskTracking(campaigns);
  const { refetchWithDelayedUpdates } = useRefetchCampaigns();

  // Check for Stripe success redirect
  useEffect(() => {
    if (sessionId) {
      // Payment was successful, invalidate organization data to get updated plan
      invalidateQuery(['organizations']);
      // Clean up URL via the router (typed search param, replaces history entry)
      navigate({ to: '/new/onboarding/generating', search: { session_id: undefined }, replace: true });
    }
  }, [sessionId, invalidateQuery, navigate]);

  // Set up the minimum display time
  useEffect(() => {
    const timer = setTimeout(() => setMinTimeReached(true), MIN_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Calculate if campaign is ready to view
  const isReady = sumTotal > 0 && taskProcessing === 0 && minTimeReached;

  // Navigate to campaigns when tasks are completed AND minimum time has passed
  useEffect(() => {
    if (!isReady || navigatedRef.current) return;
    navigatedRef.current = true;
    (async () => {
      await refetchWithDelayedUpdates();
      // Navigate to the newest campaign (highest id — array order is API order, not recency)
      const latestCampaign = campaigns?.length
        ? campaigns.reduce((latest, campaign) => (campaign.id > latest.id ? campaign : latest))
        : null;
      if (latestCampaign?.slug) {
        navigate({ to: `/campaigns/${latestCampaign.slug}` });
      } else {
        navigate({ to: '/campaigns' });
      }
    })();
  }, [isReady, campaigns, navigate, refetchWithDelayedUpdates]);

  const handleViewCampaigns = (e: React.MouseEvent) => {
    if (!isReady) {
      e.preventDefault();
    }
  };

  return (
    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
      <div className="flex justify-center">
        <div className="w-[60%]">
          <h1 className="text-3xl font-bold text-center mb-8 text-gray-900">
            Generating Your Campaigns
          </h1>
          <CampaignAdsGenerateSpinner
            showBackButton={false}
          />
          <div className="text-center mt-6">
            <Button
              onClick={handleViewCampaigns}
              disabled={!isReady}
              className="inline-flex w-[216px] py-3 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg items-center justify-center space-x-2 text-white font-medium rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              asChild={isReady}
            >
              {isReady ? (
                <Link to="/campaigns">
                  <span>VIEW CAMPAIGNS</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <>
                  <span>VIEW CAMPAIGNS</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
